import { Breadcrumb } from '@/components/Breadcrumb';
import { usePageExperienceContextOptional } from '@/features/experiences/contexts/PageExperienceContext';
import { useExperienceHomeOrg } from '@/features/experiences/hooks/useExperienceHomeOrg';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useIsLargeScreenOrSmaller } from '@/hooks/useIsBreakpoint';
import { getDashboardPath } from '@/pages/Dashboard/hooks/useWorkspaceTabs';
import { Flex, Text } from '@/ui';
import { EditableTitle } from './EditableTitle';
import { useIsHeaderNavVisible } from './useIsHeaderNavVisible';

const Separator = () => (
  <Text
    color="ink.muted"
    flexShrink={0}
    fontSize="ah-control"
    lineHeight={1}
    opacity={0.5}
  >
    /
  </Text>
);

export const BreadCrumbs = () => {
  const { trackEvent } = useAnalytics();
  const pageExperience = usePageExperienceContextOptional();
  const { experience, readOnly, isPublishedView } = pageExperience ?? {};
  const title = experience?.title;
  // The workspace crumb only resolves for members of the experience's home
  // org — including the caller's own personal org. For everyone else
  // (non-member viewers, logged-out visitors, flag off) the trail is just
  // the title.
  const { sharedOrgId, workspaceName } = useExperienceHomeOrg(experience);
  // The trail collapses to the title alone on a published page, where a crumb
  // pointing into the viewer's library misrepresents where the page lives, and
  // on tablet and below, where dropping it gives a long title room.
  const collapseTrail = useIsLargeScreenOrSmaller() || isPublishedView;
  const workspaceCrumb = collapseTrail ? undefined : workspaceName;
  const hasRoot = useIsHeaderNavVisible();
  const hasTrail = Boolean(workspaceCrumb || title);

  return (
    <Flex align="center" flex="1" gap="0.5" minW={0}>
      {hasRoot && hasTrail && <Separator />}
      {workspaceCrumb && (
        <Breadcrumb
          label={workspaceCrumb}
          maxW="160px"
          to={getDashboardPath(sharedOrgId)}
          onClick={() => trackEvent('experience:click nav-organization')}
        />
      )}
      {title && (
        <>
          {workspaceCrumb && <Separator />}
          {readOnly ? (
            <Breadcrumb isActive label={title} />
          ) : (
            <EditableTitle title={title} />
          )}
        </>
      )}
    </Flex>
  );
};
